<script>
  import { createEventDispatcher } from "svelte";
  import cloneDeep from "lodash/cloneDeep";

  import ImageInput from "#lib/sidebar/ImageInput.svelte";
  import ContenteditableInput from "#lib/sidebar/ContenteditableInput.svelte";
  import TextInput from "#lib/sidebar/TextInput.svelte";
  import CopyTextField from "#lib/sidebar/CopyTextField.svelte";
  import imageUrl from "#src/extensions/image-url.js";
  import DimtextInput from "#lib/sidebar/DimtextInput.svelte";
  import BooleanInput from "#lib/sidebar/BooleanInput.svelte";
  import SelectInput from "#lib/sidebar/SelectInput.svelte";
  import ColorInput from "#lib/sidebar/ColorInput.svelte";
  import NumberInput from "#lib/sidebar/NumberInput.svelte";
  import IntegerInput from "#lib/sidebar/IntegerInput.svelte";
  import MultiSelectInput from "#lib/sidebar/MultiSelectInput.svelte";

  import { api } from "#src/api";
  import { profile } from "#src/stores/auth.js";

  export let product;
  export let disabled = false;

  const dispatch = createEventDispatcher();

  const inputs = {
    text: TextInput,
    boolean: BooleanInput,
    dimtext: DimtextInput,
    select: SelectInput,
    multiselect: MultiSelectInput,
    number: NumberInput,
    integer: IntegerInput,
    color: ColorInput,
  };

  $: multiple = Array.isArray(product.id);

  function update(prop, value) {
    dispatch("update", { id: product.id, prop, value });
  }

  function updateType(value) {
    const data = {
      type: value,
      value: null,
    };

    if (value === "select") {
      data.options = [];
    }

    update("data", data);
  }

  function updateValue(value) {
    const data = cloneDeep(product.data);
    data.value = value;
    update("data", data);
  }

  async function uploadImage(e) {
    if (e.detail.type && e.detail.data) {
      const uuid = crypto.randomUUID();
      const filename = `${uuid}/${e.detail.filename}`;
      const { data: result, error } = await api.storage.from("images").upload(filename, e.detail.data, {
        contentType: e.detail.contentType,
      });

      if (!error) {
        const image = {
          id: uuid,
          bucket: "images",
          object_id: result.path,
          content_type: e.detail.contentType,
          filename: e.detail.filename,
        };
        dispatch("update", { id: product.id, prop: "image", value: image });
      }
    }
  }
</script>

<div class="text-xs px-4">
  {#if !multiple}
    <ImageInput
      label="Image"
      labelWidth="5.5rem"
      value={imageUrl(product.image)}
      accept="image/png, image/svg+xml"
      uploadType="arraybuffer"
      {disabled}
      on:upload={uploadImage}
      on:delete={() => update("image", null)} />
  {/if}
  <ContenteditableInput
    label="Name"
    {disabled}
    labelWidth="5.5rem"
    value={product.name}
    on:input={(e) => update("name", e.detail.value)} />
  <ContenteditableInput
    label="Description"
    {disabled}
    labelWidth="5.5rem"
    value={product.description}
    on:input={(evt) => update("description", evt.detail.value)} />
  <TextInput
    label="Classification"
    {disabled}
    labelWidth="5.5rem"
    value={product.classification}
    on:input={(e) => update("classification", e.detail.value)} />
  <BooleanInput
    label="Published"
    {disabled}
    labelWidth="5.5rem"
    value={product.published}
    on:input={(evt) => update("published", evt.detail.value)} />
  {#if $profile?.user_role === "developer" && !multiple}
    <CopyTextField label="ID" labelWidth="5.5rem" value={product.id} />
  {/if}
  <SelectInput
    label="Type"
    {disabled}
    labelWidth="5.5rem"
    options={[
      { label: "Text", value: "text" },
      { label: "Boolean", value: "boolean" },
      { label: "Dimtext", value: "dimtext" },
      { label: "Select", value: "select" },
      { label: "Multi-Select", value: "multiselect" },
      { label: "Integer", value: "integer" },
      { label: "Number", value: "number" },
      { label: "Color", value: "color" },
    ]}
    value={product.data.type}
    on:input={(e) => updateType(e.detail.value)} />
  <svelte:component
    this={inputs[product.data.type] || TextInput}
    label="Value"
    {disabled}
    labelWidth="5.5rem"
    value={product.data.value}
    on:input={(e) => updateValue(e.detail.value)} />
</div>
